import Icon from "@/icon";
import { useAppTheme } from "@/theme";
import {
  TimelineItem as MUITimelineItem,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineSeparator,
  timelineOppositeContentClasses,
} from "@mui/lab";
import { Box, Button, Container, Typography, useTheme } from "@mui/material";
import Color from "color";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface ITimelineItem {
  id: number;
  timestamp: string;
  title: string;
  description: string;
  images: string[];
  domain: string;
  url: string;
}

interface Props {
  item: ITimelineItem;
}

const TimelineItem = ({
  item: { timestamp, url, description, title, images },
}: Props) => {
  const theme = useTheme();

  return (
    <MUITimelineItem
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.2,
        },
      }}
    >
      <TimelineOppositeContent
        sx={{ m: "auto 0" }}
        variant="body2"
        color="text.secondary"
      >
        {moment(timestamp).format("hh:mm A")}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineConnector />
        <img
          src={`https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${url}/r/UoPeople/comments/zx67ao/learning_journals/&size=32`}
          alt="Domain logo"
        />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent
        sx={{ display: "flex", gap: theme.spacing(2), alignItems: "center" }}
      >
        <img src={images[0]} alt="Domain logo" height={75} />
        <Box>
          <Typography variant="h6" component="span">
            {title}
          </Typography>
          <Typography>{description}</Typography>
        </Box>
      </TimelineContent>
    </MUITimelineItem>
  );
};

enum Tabs {
  YOUR_JOURNEY = "your_journey",
  ASK_ECHONOTE = "ask_echonote",
}

const Timeline = () => {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.YOUR_JOURNEY);

  const handleTabPress = (tab: Tabs) => {
    setActiveTab(tab);
  };

  const getTabImageSrc = () => {
    switch (activeTab) {
      case Tabs.YOUR_JOURNEY:
        return "/images/popup-your-journey-tab.png";
      case Tabs.ASK_ECHONOTE:
        return "/images/popup-ask-echonote-tab.png";
    }
  };

  return (
    <Container
      sx={{
        width: "fit-content",
        display: "flex",
        gap: 2,
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          outlineWidth: 2,
          outlineStyle: "solid",
          outlineColor: Color(theme.palette.primary.main)
            .alpha(0.16)
            .toString(),
        }}
        borderRadius={theme.shape.borderRadius * 2}
        p={2}
      >
        <Box
          sx={{
            outlineWidth: 4,
            outlineStyle: "solid",
          }}
          borderRadius={theme.shape.borderRadius}
        >
          <Box
            component={"img"}
            maxWidth={"100%"}
            alignSelf={"center"}
            src={getTabImageSrc()}
            alt={"Echonote screenshot preview"}
          />
        </Box>
      </Box>
      <Box display={"flex"} gap={2} alignSelf={"center"}>
        <Button
          variant={activeTab === Tabs.YOUR_JOURNEY ? "contained" : "outlined"}
          sx={{ borderRadius: 50 }}
          startIcon={<Icon icon={"activity"} size={24} />}
          onClick={() => {
            handleTabPress(Tabs.YOUR_JOURNEY);
          }}
        >
          <Typography variant={"body1"}>
            {t("timeline.your-journey.button.label")}
          </Typography>
        </Button>
        <Button
          variant={activeTab === Tabs.ASK_ECHONOTE ? "contained" : "outlined"}
          sx={{ borderRadius: 50 }}
          startIcon={<Icon icon={"ask"} size={24} />}
          onClick={() => {
            handleTabPress(Tabs.ASK_ECHONOTE);
          }}
        >
          <Typography variant={"body1"}>
            {t("timeline.ask-echonote.button.label")}
          </Typography>
        </Button>
      </Box>
    </Container>
  );
};

export default Timeline;
